import React, { useEffect, useState, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import 'moment/locale/en-gb';
import {
  Button, message, Table,
  Typography, Row, Switch, Tag, InputNumber, Spin,
} from 'antd';
import { connect } from 'react-redux';
import { selectPackages } from '../../../../state/domain/packages/packageReducer';
import {
  editPackage, requestAllPackages, requestToggleActive,
} from '../../../../state/ui/configuration/packages/packageUiActions';
import { selectToggleStatus, selectFetchPackagesRequest } from '../../../../state/ui/configuration/packages/packageUiReducer';
import { DATE_FORMAT } from '../packageUtil';
import EditRegPackage from './EditRegPackage';
import ConfigHeading from '../../configure/components/ConfigHeading';
import '../bynx.css';
import { requestAppConfig, requestUpdateAppConfig } from '../../../../state/ui/application/appConfigUiActions';
import { selectAppConfig } from '../../../../state/domain/appConfig/appConfigReducer';
import { selectLoadAppConfigRequest, selectUpdateAppConfigRequest } from '../../../../state/ui/application/appConfigUiReducer';

const { Column } = Table;
const { Text } = Typography;

function TrialEditor({
  trialDuration, saveTrialDuration, loading, updating,
}) {
  const [newDuration, setNewDuration] = useState(trialDuration);
  useEffect(() => setNewDuration(Math.round(trialDuration / 24 / 60 / 60)),
    [setNewDuration, trialDuration]);

  return (
    <Row type="flex" className="edit-trial-row">
      <FormattedMessage id="trialDuration" />
      :
      {loading ? <Spin /> : <InputNumber value={newDuration} onChange={setNewDuration} />}
      <Button
        type="primary"
        className="save-trial-period-button"
        onClick={() => saveTrialDuration(newDuration * 24 * 60 * 60)}
        loading={loading || updating}
      >
        <FormattedMessage id="save" />
      </Button>
    </Row>
  );
}

TrialEditor.propTypes = {
  trialDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  saveTrialDuration: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  updating: PropTypes.bool.isRequired,
};

TrialEditor.defaultProps = {
  trialDuration: 14 * 24 * 60 * 60,
};
// TABLE
function PackagesTable({
  isLoading, visiblePackages, onEditButton, toggleActive, toggleStatus,
}) {
  const intl = useIntl();
  const RowTotal = () => (
    <div>
      <Text>{`${visiblePackages.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  const isActiveLoading = record => toggleStatus.find(code => code === record.code);

  // Gets the code of the package with the most recent start date (I.E the package currently being used)
  const currentPackageCode = useMemo(() => {
    const activePackages = visiblePackages.slice().filter(pack => pack.active && moment.utc(pack.packageStartDate, DATE_FORMAT).isBefore());
    const currentPackage = activePackages.find(
      pack => !activePackages.find(
        laterPack => moment.utc(pack.packageStartDate, DATE_FORMAT).isBefore(moment.utc(laterPack.packageStartDate, DATE_FORMAT)),
      ),
    );
    return currentPackage ? currentPackage.code : null;
  }, [visiblePackages]);

  const onActiveUpdate = (record) => {
    // Only toggle if the package from date is in the future
    if (moment.utc().isBefore(moment.utc(record.packageStartDate, DATE_FORMAT))) toggleActive(record);
    else message.warn(intl.formatMessage({ id: record.active ? 'packageUnPublishInvalid' : 'packagePublishInvalid' }));
  };

  function sortByTitle(a, b) {
    const aStr = `${a.title}`;
    const bStr = `${b.title}`;
    return aStr.localeCompare(bStr);
  }

  function sortByFromDate(a, b) {
    const adate = moment.utc(a.packageStartDate, DATE_FORMAT);
    const bdate = moment.utc(b.packageStartDate, DATE_FORMAT);
    if (adate.isBefore(bdate)) {
      return 1;
    }
    if (adate.isAfter(bdate)) {
      return -1;
    }
    return 0;
  }

  function sortByPublished(a, b) {
    if (a.active && !b.active) return 1;
    if (!a.active && b.active) return -1;
    return -sortByFromDate(a, b);
  }

  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visiblePackages}
      rowKey={record => record.code}
    >
      <Column
        title={<FormattedMessage id="published" />}
        dataIndex="active"
        key="active"
        sorter={sortByPublished}
        width={120}
        render={(active, record) => (
          <Row type="flex" justify="center">
            {record.code === currentPackageCode ? (
              <Tag color="green"><FormattedMessage id="active" /></Tag>
            ) : <Switch loading={!!isActiveLoading(record)} onChange={() => onActiveUpdate(record)} size="small" checked={active} />}
          </Row>
        )}
      />
      <Column
        sorter={sortByTitle}
        title={<FormattedMessage id="packageName" />}
        dataIndex="title"
        key="title"
      />
      <Column
        sorter={sortByFromDate}
        defaultSortOrder="ascend"
        title={<FormattedMessage id="startDate" />}
        dataIndex="packageStartDate"
        key="packageStartDate"
      />
      <Column
        title={<FormattedMessage id="bonusCredits" />}
        dataIndex="extraTokens"
        key="extraTokens"
      />
      {/*<Column*/}
      {/*  className="via-action-column"*/}
      {/*  align="right"*/}
      {/*  width="70px"*/}
      {/*  render={(text, record) => {*/}
      {/*    const startDate = moment.utc(record.packageStartDate, DATE_FORMAT);*/}
      {/*    const editable = !record.active || moment.utc().isBefore(startDate);*/}
      {/*    return (*/}
      {/*      <Row>*/}
      {/*        <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">*/}
      {/*          <FormattedMessage id={editable ? 'edit' : 'view'} />*/}
      {/*        </Button>*/}
      {/*      </Row>*/}
      {/*    );*/}
      {/*  }}*/}
      {/*/>*/}
    </Table>
  );
}

PackagesTable.propTypes = {
  isLoading: PropTypes.bool,
  onEditButton: PropTypes.func.isRequired,
  visiblePackages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      packageStartDate: PropTypes.string.isRequired,
      packageEndDate: PropTypes.string,
      currencyCode: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      mainTokens: PropTypes.number.isRequired,
      extraTokens: PropTypes.number,
      active: PropTypes.bool.isRequired,
    }),
  ),
  toggleActive: PropTypes.func.isRequired,
  toggleStatus: PropTypes.arrayOf(PropTypes.number),
};

PackagesTable.defaultProps = {
  isLoading: false,
  visiblePackages: [],
  toggleStatus: [],
};

function RegPackages({
  isLoading, loadPageData, packages, trialDuration, loadTrialDuration, trialLoading,
  onCreateOrEdit, toggleActive, toggleStatus, saveTrialDuration, updatingTrialDuration,
}) {
  // Load users on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
    loadTrialDuration();
  }, [loadPageData, loadTrialDuration]);

  const intl = useIntl();

  const [visiblePackages, setVisiblePackages] = useState([]);
  useEffect(() => {
    const tempPackages = packages.slice();
    setVisiblePackages(tempPackages.filter(strPackage => strPackage.registration).map(strPackage => ({
      ...strPackage,
      packageStartDate: moment.utc(strPackage.packageStartDate).format(DATE_FORMAT),
    })));
  }, [packages]);


  const CreateButton = () => (
    <Button type="primary" onClick={() => onCreateOrEdit(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  const startDateDupeCheck = (code, startDate) => !visiblePackages.find(
    pack => pack.active && (pack.code !== code) && moment.utc(pack.packageStartDate, DATE_FORMAT).isSame(startDate),
  );

  const toggleActiveRecord = (record) => {
    if (!startDateDupeCheck(record.code, moment.utc(record.packageStartDate, DATE_FORMAT))) {
      message.warn(intl.formatMessage({ id: 'registrationPackageDuplicate' }));
    } else {
      toggleActive(packages.find(pack => pack.code === record.code));
    }
  };

  return (
    <ConfigHeading
      titleId="registrationPackages"
      actions={<CreateButton />}
    >
      <TrialEditor
        trialDuration={trialDuration}
        saveTrialDuration={saveTrialDuration}
        loading={trialLoading}
        updating={updatingTrialDuration}
      />
      <PackagesTable
        isLoading={isLoading}
        visiblePackages={visiblePackages}
        onEditButton={onCreateOrEdit}
        toggleActive={toggleActiveRecord}
        toggleStatus={toggleStatus}
        intl={intl}
      />
      <EditRegPackage startDateDupeCheck={startDateDupeCheck} />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
RegPackages.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  packages: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    packageStartDate: PropTypes.string.isRequired,
    packageEndDate: PropTypes.string,
    currencyCode: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    mainTokens: PropTypes.number.isRequired,
    extraTokens: PropTypes.number,
    active: PropTypes.bool.isRequired,
    registration: PropTypes.bool,
  })),
  onCreateOrEdit: PropTypes.func.isRequired,
  toggleActive: PropTypes.func.isRequired,
  toggleStatus: PropTypes.arrayOf(PropTypes.number),
  trialDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loadTrialDuration: PropTypes.func.isRequired,
  saveTrialDuration: PropTypes.func.isRequired,
  trialLoading: PropTypes.bool.isRequired,
  updatingTrialDuration: PropTypes.bool.isRequired,
};

RegPackages.defaultProps = {
  isLoading: false,
  packages: [],
  toggleStatus: [],
  trialDuration: 0,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectFetchPackagesRequest(state);
  const trialDurationObject = selectAppConfig(state, 'NEW_GROUP_TRIAL_LENGTH');
  const loadTrialDurationRequest = selectLoadAppConfigRequest(state, 'NEW_GROUP_TRIAL_LENGTH');
  const updateTrialDurationRequest = selectUpdateAppConfigRequest(state, 'NEW_GROUP_TRIAL_LENGTH');

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    packages: selectPackages(state),
    toggleStatus: selectToggleStatus(state),
    trialDuration: trialDurationObject ? trialDurationObject.applicationConfigValue : null,
    trialLoading: loadTrialDurationRequest ? loadTrialDurationRequest.isLoading : false,
    updatingTrialDuration: updateTrialDurationRequest ? updateTrialDurationRequest.isLoading : false,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestAllPackages()),
  onCreateOrEdit: pack => dispatch(editPackage(pack ? pack.code : null)),
  toggleActive: pack => dispatch(requestToggleActive(pack)),
  loadTrialDuration: () => dispatch(requestAppConfig('NEW_GROUP_TRIAL_LENGTH')),
  saveTrialDuration: newDurationInSeconds => dispatch(requestUpdateAppConfig('NEW_GROUP_TRIAL_LENGTH', newDurationInSeconds)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(RegPackages));
